/* eslint-disable no-console */
import {
  BaseTrackingEventData, TrackingEvent, TrackingServiceConfig,
} from '@type/tracking';
import { setCookie, getCookie, deleteCookie } from 'cookies-next';
import { TRACKING_SESSION_ID_NAME, COOKIE_DOMAIN } from './constants';
import { isBot } from './botDetection';

/** Default configuration values */
const DEFAULT_CONFIG = {
  batchSize: 10,
  batchTimeout: 5000, // 5 seconds
  debug: false,
};

/** Module-level variables (private state) */
let config: Required<TrackingServiceConfig>;
let eventQueue: TrackingEvent[] = [];
let processingTimeout: NodeJS.Timeout | null = null;
let isProcessing = false;
let sessionId: string;
let isInitialized = false;
let pendingEvents: BaseTrackingEventData[] = []; // Queue for events before initialization

/**
 * @description Flush the event queue using navigator.sendBeacon (for unload/visibilitychange)
 */
function flushQueueWithBeacon(): void {
  if (typeof navigator === 'undefined') {
    console.warn('[TrackingService] Attempted to flush queue using sendBeacon but navigator is undefined');
    return;
  }
  if (!eventQueue.length) return;

  try {
    const payload = JSON.stringify(eventQueue);
    const blob = new Blob([payload], { type: 'application/json' });
    const sent = navigator.sendBeacon(config.endpoint, blob);
    if (config.debug) {
      console.log(`[TrackingService] Flushed ${eventQueue.length} events with sendBeacon: ${sent}`);
    }
    eventQueue = [];
  } catch (e) {
    if (config.debug) {
      console.error('[TrackingService] sendBeacon failed:', e);
    }
  }
}

/**
 * @description Generate a UUID for the session ID.
 * This function uses the `crypto` API if available, or falls back to a more secure custom implementation.
 * Follows RFC4122 for UUID v4 generation.
 *
 * @example
 * // Generate a UUID
 * const uuid = generateUUID();
 */
const generateUUID = (): string => {
  // Use crypto.randomUUID if available (modern browsers)
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  // Fallback for environments with no crypto support
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.floor(Math.random() * 16);
    const v = c === 'x' ? r : (r % 4) + 8;
    return v.toString(16);
  });
};

/**
 * @description Get the existing session ID from sessionStorage or create a new one.
 *
 * @example
 * // Get or create a session ID
 * const sessionId = getOrCreateSessionId();
 */
const getOrCreateSessionId = (): string => {
  if (typeof window === 'undefined') {
    return 'server-side';
  }

  // Try to get the session ID from cookies (accessible by both client and server). If not found, check sessionStorage.
  let storedSessionId = getCookie(TRACKING_SESSION_ID_NAME) as string | null;
  if (!storedSessionId) {
    storedSessionId = sessionStorage.getItem(TRACKING_SESSION_ID_NAME);
  }

  // If no session ID exists, create a new one and store it in a cookie and sessionStorage.
  // No expiration means it's a session cookie that expires when the browser is closed.
  if (!storedSessionId) {
    storedSessionId = generateUUID();

    sessionStorage.setItem(TRACKING_SESSION_ID_NAME, storedSessionId);

    void setCookie(TRACKING_SESSION_ID_NAME, storedSessionId, {
      secure: true,
      domain: COOKIE_DOMAIN,
      path: '/',
    });
  }

  return storedSessionId;
};

/**
 * @description Send a batch of events to the API.
 * This function handles the actual API request to send the events.
 *
 * @param events - Array of TrackingEvent objects to be sent.
 *
 * @example
 * // Send a batch of events to the API
 * sendToAPI([
 *   {
 *      eventData: { name: 'page_view', category: 'engagement', path: '/home' },
 *      timestamp: Date.now(),
 *      sessionId,
 *   },
 *   {
 *      eventData: { name: 'button_click', category: 'interaction', buttonId: 'apply-job' },
 *      timestamp: Date.now(),
 *      sessionId,
 *   }
 * ]);
 */
const sendToAPI = async (events: TrackingEvent[]): Promise<void> => {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000);

    const response = await fetch(config.endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(events),
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      console.error(`API request failed with status ${response.status}`);
    }
  } catch (error) {
    if (error && typeof error === 'object' && 'name' in error && error?.name === 'AbortError') {
      console.warn('[TrackingService] Request timed out');
    } else if (error instanceof TypeError && error.message === 'Failed to fetch') {
      console.warn('[TrackingService] Network error - tracking endpoint may be unavailable');
    } else {
      console.error('[TrackingService] API request error:', error);
    }
  }
};


/**
 * @description Process the event queue
 * This function takes a batch of events from the queue and sends them to the API.
 * It handles re-queuing on failure and ensures that events are processed in batches.
 *
 * @param callbackFn - Optional callback function to call after processing the queue. It's the 'scheduleProcessing' function.
 *
 * @example
 * // Process the event queue
 * processQueue();
 *
 * // Process the event queue with a callback function
 * processQueue(scheduleProcessing);
 */
function processQueue(callbackFn?: () => void): void {
  // Don't process if already processing or queue is empty
  if (isProcessing || eventQueue.length === 0) {
    return;
  }

  // Set processing flag to prevent concurrent processing
  isProcessing = true;

  // Take a batch of events from the queue
  const batch = eventQueue.splice(0, config.batchSize);

  // Debug logging
  if (config.debug) {
    console.log('[TrackingService] Processing batch of', batch.length, 'events');
  }

  // Send the batch to the API
  sendToAPI(batch)
    .then(() => {
      if (config.debug) {
        console.log('[TrackingService] Batch sent successfully');
      }
    })
    .catch((error) => {
      // Re-queue events on failure
      if (config.debug) {
        console.error('[TrackingService] Failed to send batch:', error);
        console.log('[TrackingService] Re-queuing events');
      }

      // Add events back to the beginning of the queue
      eventQueue.unshift(...batch);
    })
    .finally(() => {
      isProcessing = false;

      // If there are still events in the queue, schedule processing
      if (eventQueue.length > 0 && callbackFn) {
        callbackFn();
      }
    });
}

/**
 * @description Schedule processing of the event queue.
 * This function checks the queue length and either processes immediately if the batch size is reached,
 * or sets a timeout to process after a specified delay.
 *
 * @example
 * // Schedule processing of the event queue
 * scheduleProcessing();
 */
function scheduleProcessing(): void {
  // If we've reached the batch size, process immediately
  if (eventQueue.length >= config.batchSize) {
    if (processingTimeout) {
      clearTimeout(processingTimeout);
      processingTimeout = null;
    }

    processQueue(scheduleProcessing);
    return;
  }

  // Otherwise, set a timeout to process the queue after the batch timeout
  if (!processingTimeout) {
    processingTimeout = setTimeout(() => {
      processQueue(scheduleProcessing);
      processingTimeout = null;
    }, config.batchTimeout);

    if (config.debug) {
      console.log('[TrackingService] Processing scheduled in', config.batchTimeout, 'ms');
    }
  }
}

/**
 * @description Process pending events that were queued before initialization.
 * This function converts pending events to TrackingEvents and adds them to the main queue.
 *
 * @example
 * // Process pending events after initialization
 * processPendingEvents();
 */
function processPendingEvents(): void {
  if (pendingEvents.length === 0) return;

  if (config.debug) {
    console.log('[TrackingService] Processing', pendingEvents.length, 'pending events');
  }

  // Convert pending events to TrackingEvents and add to main queue
  const trackingEvents: TrackingEvent[] = pendingEvents.map((eventData) => ({
    ...eventData,
    timestamp: Date.now(),
    sessionId,
  }));

  eventQueue.push(...trackingEvents);
  pendingEvents = []; // Clear pending events

  if (config.debug) {
    console.log('[TrackingService] Added pending events to main queue. Queue size:', eventQueue.length);
  }

  // Schedule processing of the newly added events
  scheduleProcessing();
}

/**
 * @description Initialize the tracking service.
 * This function sets up the tracking service with the provided configuration,
 * initializes the session ID, and prepares the event queue for tracking.
 * @param userConfig - User-provided configuration for the tracking service.
 *
 * @example
 * // Initialize tracking service with custom configuration
 * initializeTracking({
 *   endpoint: '/api/tracking',
 *   batchSize: 20,
 *   batchTimeout: 10000,
 *   debug: process.env.NODE_ENV === 'development',
 * });
 */
export const initializeTracking = (userConfig: TrackingServiceConfig): void => {
  if (isInitialized) {
    console.warn('[TrackingService] Already initialized');
    return;
  }

  // Merge user config with defaults
  config = {
    ...DEFAULT_CONFIG,
    ...userConfig,
  };

  // Initialize session ID
  sessionId = getOrCreateSessionId();
  isInitialized = true;

  // Process any pending events that were queued before initialization
  processPendingEvents();

  // Register sendBeacon flush on pagehide event
  if (typeof window !== 'undefined') {
    window.addEventListener('pagehide', flushQueueWithBeacon);
  }

  // Log initialization in debug mode
  if (config.debug) {
    console.log('[TrackingService] Initialized with config:', config);
    console.log('[TrackingService] Session ID:', sessionId);
  }
};

/**
 * @description Track a user interaction event. Main entry point for tracking events.
 * This function adds an event to the tracking queue and schedules processing.
 * The UTM parameters from the current URL are automatically added to the event metadata.
 *
 * @example
 * import { trackEvent } from '@services/tracking';
 *
 * // usage with the useEffect hook for tracking page views
 * useEffect(() => {
 *   // Small delay (next tick) to ensure tracking is initialized. The Context is initialized with useEffect also,
 *   // so this ensures the tracking service is ready before sending the event.
 *   const timer = setTimeout(() => {
 *     trackEvent({
 *       eventType: 'view',
 *       entityType: 'job',
 *       // this is usually the job/resume slug or id
 *       entityId: '12345',
 *       viewContext: 'detail',
 *     });
 *   }, 0);
 *
 *   return () => clearTimeout(timer);
 * }, []);
 *
 * // usage inside a function for tracking user actions
 * const handleJobApplyClick = () => {
 *   trackEvent({
 *     eventType: 'click',
 *     entityType: 'job',
 *     // this is usually the job/resume slug or id
 *     entityId: '12345',
 *     action: 'apply',
 *     listType: 'search',
 *     position: 3,
 *     userId: currentUser.id,
 *   });
 *
 *   // Continue with button click logic...
 * };
 */
export const trackEvent = <T extends BaseTrackingEventData>(eventData: T): void => {
  // Do not track bots/automated clients
  if (isBot()) return;
  if (!isInitialized) {
    // Queue the event for later processing when initialization completes
    pendingEvents.push(eventData);
    return;
  }

  const event: TrackingEvent = {
    ...eventData,
    timestamp: Date.now(),
    sessionId,
  };

  eventQueue.push(event);

  if (config.debug) {
    console.log('[TrackingService] Event added to queue:', event);
    console.log('[TrackingService] Queue size:', eventQueue.length);
  }

  scheduleProcessing();
};

/**
 * @description Track an event immediately using sendBeacon (bypasses batching/queue).
 * The main purpose of this function is to be used when you want to ensure the event is tracked
 * when the tab/browser is closed (eg: listening for pagehide or unload events).
 */
export const trackEventWithBeacon = <T extends BaseTrackingEventData>(eventData: T): void => {
  // Do not track bots/automated clients
  if (isBot()) return;
  if (!isInitialized) {
    // Queue the event for later processing when initialization completes
    pendingEvents.push(eventData);
    return;
  }

  if (typeof navigator === 'undefined') {
    console.warn('[TrackingService] Attempted to track event using sendBeacon but navigator is undefined:', eventData);
    return;
  }

  const event: TrackingEvent = {
    ...eventData,
    timestamp: Date.now(),
    sessionId,
  };

  try {
    const payload = JSON.stringify([event]);
    const blob = new Blob([payload], { type: 'application/json' });
    navigator.sendBeacon(config.endpoint, blob);
    if (config.debug) {
      console.log('[TrackingService] Sent event immediately with sendBeacon:', event);
    }
  } catch (e) {
    if (config.debug) {
      console.error('[TrackingService] Immediate sendBeacon failed:', e);
    }
  }
};

/**
 * @description Get the current session ID.
 *
 * @example
 * // Get the session ID
 * const sessionId = getSessionId();
 */
export const getSessionId = (): string => {
  if (!isInitialized) {
    console.warn('[TrackingService] Attempted to get session ID before initialization');
    return '';
  }

  return sessionId;
};

/**
 * @description Get the current queue length.
 *
 * @example
 * // Get the length of the event queue
 * const queueLength = getQueueLength();
 */
export const getQueueLength = (): number => eventQueue.length;

/**
 * @description Force process the queue immediately.
 * This function is useful for testing or when you need to ensure that events are sent immediately,
 * bypassing the batch size and timeout logic.
 *
 * @example
 * // Force process the event queue
 * forceProcessQueue();
 */
export const forceProcessQueue = (): void => {
  if (!isInitialized) {
    console.warn('[TrackingService] Attempted to force process queue before initialization');
    return;
  }

  processQueue(scheduleProcessing);
};

/**
 * @description Reset the tracking service (for testing purposes).
 *
 * @example
 * // Reset the tracking service
 * resetTracking();
 */
export const resetTracking = (): void => {
  eventQueue = [];
  pendingEvents = [];
  if (processingTimeout) {
    clearTimeout(processingTimeout);
    processingTimeout = null;
  }
  isProcessing = false;
  isInitialized = false;
};

/**
 * @description Clear the tracking session ID from both cookie and sessionStorage
 */
export const clearTrackingSession = (): void => {
  if (typeof window === 'undefined') return;
  void deleteCookie(TRACKING_SESSION_ID_NAME, { path: '/' });
  sessionStorage.removeItem(TRACKING_SESSION_ID_NAME);
};
